import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  Table,
  Card,
  Container,
  TableBody,
  TableContainer,
  IconButton,
  Box,
  useTheme,
  Typography,
  TableRow,
} from "@mui/material";
import {
  TABLE_HEAD_PORTFOLIO_SHORT,
  TABLE_HEAD_PORTFOLIO_LONG,
  applySortFilter,
  getComparator,
} from "../../component/Methods";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import Grid from "@mui/material/Grid";
import PositionTable from "./PositionTable";
import PositionPopUp from "./Popup/PositionPopUp";
import VantageListHead from "../../component/ListHead/VantageListHead";

// Virtual scrolling constants
const ROW_HEIGHT = 35; // Height of each row in pixels
const BUFFER_SIZE = 15; // Number of extra rows to render above/below visible area

const AssetPositions = React.memo((props) => {
  const { data, fundVal, StrategeyVal } = props;
  const [long, setlong] = useState([]);
  const [expandLong, setExpandLong] = useState([]);
  const [short, setshort] = useState([]);
  const [expandShort, setExpandShort] = useState([]);
  const [orderByShort, setOrderByShort] = useState("size");
  const [orderByLong, setOrderByLong] = useState("size");
  const [longSortOrder, setLongSortOrder] = useState("desc");
  const [longFilterName, setLongFilterName] = useState("");
  const [shortSortOrder, setShortSortOrder] = useState("desc");
  const [shortFilterName, setShortFilterName] = useState("");
  const [isGridView, setIsGridView] = useState(false);
  const [isGridViewShort, setIsGridViewShort] = useState(false);

  // Virtual scrolling refs
  const longTableRef = useRef(null);
  const shortTableRef = useRef(null);
  const [longScrollTop, setLongScrollTop] = useState(0);
  const [shortScrollTop, setShortScrollTop] = useState(0);

  // Memoize the data processing to prevent unnecessary recalculations
  const processedData = useMemo(() => {
    const filteredData = data;

    const long = filteredData?.filter((i) => i?.deltaAdjustedShareCount > 0);
    const short = filteredData?.filter((i) => i?.deltaAdjustedShareCount <= 0);

    const renamedShort = short
      ?.map((item) => ({
        short_size: item?.size,
        short_current: item?.sharePrice,
        short_deltaadjustedsharecount: item?.deltaAdjustedShareCount,
        short_ch: item?.ch,
        short_pL: parseFloat(item?.pL),
        short_cost: parseFloat(item?.avgCost),
        short_last: item?.last,
        short_tickerSymbol: item?.tickerSymbol,
        short_chpercentage: parseFloat(item?.chPercetage),
        short_pl_avg_cost: parseFloat(
          (item?.last - item?.avgCost) * item?.deltaAdjustedShareCount
        ),
        short_percentage_avg_cost: parseFloat(
          ((item?.pL * item?.avgCost) / item?.avgCost) * 100
        ),
        dataSource: item?.dataSource,
        fetched: item?.fetched,
        valid: item?.valid,
        short_tdg: item?.tdg,
        short_custodian: item?.custodian,
      }))
      ?.sort((a, b) => b?.short_size - a?.short_size);

    const renamedLong = long
      ?.map((item) => ({
        long_size: item?.size,
        long_deltaadjustedsharecount: item?.deltaAdjustedShareCount,
        long_ch: item?.ch,
        long_pL: parseFloat(item?.pL),
        long_cost: parseFloat(item?.avgCost),
        long_last: item?.last,
        long_current: item?.sharePrice,
        long_tickerSymbol: item?.tickerSymbol,
        long_chpercentage: parseFloat(item?.chPercetage),
        long_pl_avg_cost: parseFloat(
          (item?.last - item?.avgCost) * item?.deltaAdjustedShareCount
        ),
        long_percentage_avg_cost: parseFloat(
          ((item?.pL * item?.avgCost) / item?.avgCost) * 100
        ),
        dataSource: item?.dataSource,
        fetched: item?.fetched,
        valid: item?.valid,
        long_tdg: item?.tdg,
        long_custodian: item?.custodian,
      }))
      ?.sort((a, b) => b?.long_size - a?.long_size);

    return { renamedLong, renamedShort };
  }, [data]);

  useEffect(() => {
    const { renamedLong, renamedShort } = processedData;

    setExpandLong(renamedLong);
    setExpandShort(renamedShort);

    const filteredUsersLong = applySortFilter(
      renamedLong,
      getComparator(longSortOrder, orderByLong),
      longFilterName
    );

    const filteredUsersShort = applySortFilter(
      renamedShort,
      getComparator(shortSortOrder, orderByShort),
      shortFilterName
    );

    setlong(filteredUsersLong);
    setshort(filteredUsersShort);
  }, [
    processedData,
    longSortOrder,
    orderByLong,
    orderByShort,
    longFilterName,
    shortSortOrder,
    shortFilterName,
  ]);

  // Virtual scrolling calculations for long table
  const longVirtualData = useMemo(() => {
    if (!long || long.length === 0) return { visibleRows: [], totalHeight: 0 };

    // Calculate actual container height (50vh = ~400px, but let's be more precise)
    const containerHeight = longTableRef.current
      ? longTableRef.current.clientHeight
      : 400;
    const startIndex = Math.max(
      0,
      Math.floor(longScrollTop / ROW_HEIGHT) - BUFFER_SIZE
    );
    const endIndex = Math.min(
      long.length,
      Math.ceil((longScrollTop + containerHeight) / ROW_HEIGHT) + BUFFER_SIZE
    );

    const visibleRows = long.slice(startIndex, endIndex).map((row, index) => ({
      ...row,
      virtualIndex: startIndex + index,
    }));

    return {
      visibleRows,
      totalHeight: long.length * ROW_HEIGHT,
      startIndex,
      endIndex,
    };
  }, [long, longScrollTop]);

  // Virtual scrolling calculations for short table
  const shortVirtualData = useMemo(() => {
    if (!short || short.length === 0)
      return { visibleRows: [], totalHeight: 0 };

    // Calculate actual container height (50vh = ~400px, but let's be more precise)
    const containerHeight = shortTableRef.current
      ? shortTableRef.current.clientHeight
      : 400;
    const startIndex = Math.max(
      0,
      Math.floor(shortScrollTop / ROW_HEIGHT) - BUFFER_SIZE
    );
    const endIndex = Math.min(
      short.length,
      Math.ceil((shortScrollTop + containerHeight) / ROW_HEIGHT) + BUFFER_SIZE
    );

    const visibleRows = short.slice(startIndex, endIndex).map((row, index) => ({
      ...row,
      virtualIndex: startIndex + index,
    }));

    return {
      visibleRows,
      totalHeight: short.length * ROW_HEIGHT,
      startIndex,
      endIndex,
    };
  }, [short, shortScrollTop]);

  // Scroll handlers
  const handleLongScroll = useCallback((event) => {
    setLongScrollTop(event.target.scrollTop);
  }, []);

  const handleShortScroll = useCallback((event) => {
    setShortScrollTop(event.target.scrollTop);
  }, []);

  // Force recalculation when refs are available
  useEffect(() => {
    if (longTableRef.current) {
      setLongScrollTop(0);
    }
  }, [longTableRef.current]);

  useEffect(() => {
    if (shortTableRef.current) {
      setShortScrollTop(0);
    }
  }, [shortTableRef.current]);

  const handleRequestSort = (event, property) => {
    if (property.startsWith("long_")) {
      const isAsc = longSortOrder === "asc";
      setLongSortOrder(isAsc ? "desc" : "asc");
      setOrderByLong(property);
    } else if (property.startsWith("short_")) {
      const isAsc = shortSortOrder === "asc";
      setShortSortOrder(isAsc ? "desc" : "asc");
      setOrderByShort(property);
    }
  };

  const toggleGridView = () => {
    setIsGridView(!isGridView);
  };
  const toggleGridViewShort = () => {
    setIsGridViewShort(!isGridViewShort);
  };
  const theme = useTheme();
  const TABLE_HEAD_PORTFOLIO_SHORT_SLICED = [
    ...TABLE_HEAD_PORTFOLIO_SHORT.slice(0, 8),
    ...TABLE_HEAD_PORTFOLIO_SHORT.slice(10),
  ];
  const TABLE_HEAD_PORTFOLIO_LONG_SLICED = [
    ...TABLE_HEAD_PORTFOLIO_LONG.slice(0, 8),
    ...TABLE_HEAD_PORTFOLIO_LONG.slice(10),
  ];
  return (
    <Container maxWidth="xxl" sx={{ marginLeft: "-40px", mt: 1 }}>
      <Grid container spacing={2}>
        <Grid item xs={6} md={6}>
          <Box
            sx={{
              border: `1px solid ${theme.palette.lightGrey.tableHeader}`,
              borderRadius: "2px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: theme.palette.green.longPositions,
                px: 1,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ color: theme.palette.lightGrey.textColor }}
              >
                Longs Positions ({long.length})
              </Typography>
              <IconButton
                onClick={toggleGridView}
                aria-label="Toggle GridView"
                sx={{ height: "20px", width: "20px" }}
              >
                <FullscreenIcon sx={{ height: "15px", width: "15px" }} />
              </IconButton>
            </Box>

            <TableContainer
              onScroll={handleLongScroll}
              ref={longTableRef}
              sx={{
                flex: 1,
                overflow: "auto",
                maxHeight: "calc(100vh - 250px)",
              }}
            >
              <Table
                size="small"
                stickyHeader
                aria-label="a dense table"
                sx={{
                  height: "max-content",
                  maxHeight: "55px",
                  borderCllapse: "collapse",
                }}
              >
                <VantageListHead
                  headLabel={TABLE_HEAD_PORTFOLIO_LONG_SLICED}
                  noStatus={true}
                  title={true}
                  order={longSortOrder}
                  orderBy={orderByLong}
                  onRequestSort={handleRequestSort}
                />

                <TableBody>
                  {/* Virtual scrolling spacer */}
                  {longVirtualData.startIndex > 0 && (
                    <TableRow
                      style={{
                        height: longVirtualData.startIndex * ROW_HEIGHT,
                      }}
                    >
                      <td colSpan={TABLE_HEAD_PORTFOLIO_LONG_SLICED.length} />
                    </TableRow>
                  )}

                  {/* Visible rows only */}
                  {longVirtualData.visibleRows?.map((row, i) => (
                    <PositionTable
                      key={`${row.long_tickerSymbol}-${row.virtualIndex}`}
                      row={row}
                      fundVal={fundVal}
                      strategyVal={StrategeyVal}
                      visible={false}
                      positionType="long"
                    />
                  ))}

                  {/* Virtual scrolling spacer */}
                  {longVirtualData.endIndex < long.length && (
                    <TableRow
                      style={{
                        height:
                          (long.length - longVirtualData.endIndex) * ROW_HEIGHT,
                      }}
                    >
                      <td colSpan={TABLE_HEAD_PORTFOLIO_LONG_SLICED.length} />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <br />
          </Box>
        </Grid>
        <Grid item xs={6} md={6}>
          <Box
            sx={{
              border: `1px solid ${theme.palette.lightGrey.tableHeader}`,
              borderRadius: "4px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: theme.palette.red.shortPositions,
                px: 1,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ color: theme.palette.lightGrey.textColor }}
              >
                Shorts Positions ({short.length})
              </Typography>
              <IconButton
                onClick={toggleGridViewShort}
                aria-label="Toggle GridView"
                sx={{ height: "20px", width: "20px" }}
              >
                <FullscreenIcon sx={{ height: "15px", width: "15px" }} />
              </IconButton>
            </Box>

            <TableContainer
              onScroll={handleShortScroll}
              ref={shortTableRef}
              sx={{
                flex: 1,
                flexShrink: 0,
                overflow: "auto",
                maxHeight: "calc(100vh - 250px)",
              }}
            >
              <Table
                size="small"
                stickyHeader
                aria-label="a dense table"
                sx={{
                  height: "max-content",
                  maxHeight: "55px",
                  borderCllapse: "collapse",
                }}
              >
                <VantageListHead
                  headLabel={TABLE_HEAD_PORTFOLIO_SHORT_SLICED}
                  noStatus={true}
                  title={true}
                  order={shortSortOrder}
                  orderBy={orderByShort}
                  onRequestSort={handleRequestSort}
                />

                <TableBody>
                  {/* Virtual scrolling spacer */}
                  {shortVirtualData.startIndex > 0 && (
                    <TableRow
                      style={{
                        height: shortVirtualData.startIndex * ROW_HEIGHT,
                      }}
                    >
                      <td colSpan={TABLE_HEAD_PORTFOLIO_SHORT_SLICED.length} />
                    </TableRow>
                  )}

                  {/* Visible rows only */}
                  {shortVirtualData.visibleRows?.map((row, i) => (
                    <PositionTable
                      key={`${row.short_tickerSymbol}-${row.virtualIndex}`}
                      row={row}
                      fundVal={fundVal}
                      strategyVal={StrategeyVal}
                      visible={false}
                      positionType="short"
                    />
                  ))}

                  {/* Virtual scrolling spacer */}
                  {shortVirtualData.endIndex < short.length && (
                    <TableRow
                      style={{
                        height:
                          (short.length - shortVirtualData.endIndex) *
                          ROW_HEIGHT,
                      }}
                    >
                      <td colSpan={TABLE_HEAD_PORTFOLIO_SHORT_SLICED.length} />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <br />
          </Box>
        </Grid>
      </Grid>
      <PositionPopUp
        open={isGridView}
        setOpen={setIsGridView}
        data={expandLong}
        positionType="long"
        title="Longs Positions"
      />
      <PositionPopUp
        open={isGridViewShort}
        setOpen={setIsGridViewShort}
        data={expandShort}
        positionType="short"
        title="Shorts Positions"
      />
    </Container>
  );
});

// Memoize the component to prevent unnecessary re-renders
export default React.memo(AssetPositions);
